import { useState } from 'react';

import { reasonFrom } from '../api/reason';
import { Badge, Button, Field, Input, Modal, Notice, Panel, useToast } from '@efira/ui';

import {
  useActivateChannel,
  useAttachChannel,
  useChannels,
  useDetachChannel,
  type AttachError,
  type Channel,
} from '../api/queries';

/**
 * Зона каналов TikTok.
 *
 * Правила - форк F6 (docs/DECISIONS.md). Экран обязан объяснять КАЖДЫЙ отказ
 * своим текстом и своим действием: «удалите лишний канал», «этот канал занят»
 * и «подождите до такой-то даты» - три разные ситуации, и общее «не удалось
 * добавить» не помогает ни в одной.
 *
 * Настройки живут на АККАУНТЕ, а не на канале: смена канала не теряет ничего.
 * Это заявленное преимущество над конкурентом, у которого переключению
 * посвящена одна строка документации.
 */
export function ChannelsZone() {
  const toast = useToast();
  const { data, isLoading } = useChannels();
  const attach = useAttachChannel();
  const detach = useDetachChannel();
  const activate = useActivateChannel();

  const [handle, setHandle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [detaching, setDetaching] = useState<Channel | null>(null);

  const channels = data?.channels ?? [];
  const limit = data?.limit ?? null;
  const atLimit = limit !== null && channels.length >= limit;

  /**
   * Текст отказа по машинному коду.
   *
   * У известных кодов свой текст с действием - это и есть смысл машинного
   * кода. У НЕИЗВЕСТНОГО кода берётся сообщение сервера, а не общая фраза:
   * общая фраза здесь означала бы, что новый вид отказа, добавленный на
   * сервере, будет показан как «не удалось» и потеряет причину.
   */
  function describe(rejection: AttachError): string {
    switch (rejection.error) {
      case 'channel_limit_reached':
        return `На вашем тарифе можно подключить ${rejection.limit ?? 3} канала. Отвяжите лишний или перейдите на Pro — там ограничения нет.`;
      case 'bound_to_other_account':
        return 'Этот канал уже подключён к другому аккаунту Efira. Один канал работает только с одним аккаунтом.';
      case 'rebind_window_active': {
        const date =
          rejection.availableAt === undefined
            ? null
            : new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(
                new Date(rejection.availableAt),
              );
        return date === null
          ? 'Канал недавно был в эфире на другом аккаунте. Перенос станет доступен позже.'
          : `Канал недавно был в эфире на другом аккаунте. Перенести его сюда можно будет после ${date}.`;
      }
      default:
        return reasonFrom(rejection, 'Не удалось добавить канал.');
    }
  }

  function onAdd(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    attach.mutate(handle, {
      onSuccess: () => {
        setHandle('');
        toast.show({ tone: 'success', title: 'Канал добавлен' });
      },
      onError: (rejection) => {
        setError(describe(rejection as unknown as AttachError));
      },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="TikTok-каналы"
        subtitle={
          limit === null
            ? 'На Pro число каналов не ограничено'
            : `Подключено ${channels.length} из ${limit} на бесплатном тарифе`
        }
      >
        {error !== null ? (
          <div>
            <Notice tone="warning" title="Канал не добавлен" live>
              {error}
            </Notice>
          </div>
        ) : null}

        <form onSubmit={onAdd} noValidate className="flex flex-col gap-3">
          <Field
            label="Имя канала"
            hint="Как в профиле TikTok, без «собачки». Подтверждение владения произойдёт при первом эфире."
          >
            {(props) => (
              <Input
                {...props}
                value={handle}
                onChange={(event) => {
                  setHandle(event.target.value);
                }}
                placeholder="myusername"
                autoComplete="off"
                disabled={atLimit}
              />
            )}
          </Field>

          <div>
            <Button
              type="submit"
              variant="primary"
              size="m"
              loading={attach.isPending}
              disabled={handle.trim() === '' || atLimit}
              title={
                atLimit
                  ? 'Достигнут лимит тарифа'
                  : handle.trim() === ''
                    ? 'Введите имя канала'
                    : undefined
              }
            >
              Добавить канал
            </Button>
          </div>
        </form>

        {atLimit ? (
          <p className="text-foreground/80 text-sm">
            Достигнут лимит тарифа. Отвяжите ненужный канал — отвязка свободна, и вернуть его на
            этот же аккаунт можно в любой момент без ожидания.
          </p>
        ) : null}
      </Panel>

      <Panel title="Подключённые каналы">
        {isLoading ? <p className="text-foreground/80 text-sm">Загрузка…</p> : null}

        {!isLoading && channels.length === 0 ? (
          <p className="text-foreground/80 text-sm">
            Здесь появятся ваши каналы. Добавьте первый — и сможете подключиться к эфиру во вкладке
            «Эфир».
          </p>
        ) : null}

        <ul className="divide-foreground/10 flex flex-col divide-y">
          {channels.map((channel) => (
            <li
              key={channel.id}
              className="flex flex-col items-start gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:gap-3"
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="font-semibold">@{channel.handle}</span>
                <span className="text-foreground/80 text-sm">
                  {channel.lastLiveAt === null
                    ? 'ни разу не выходил в эфир'
                    : `последний эфир: ${new Intl.DateTimeFormat('ru-RU', {
                        day: 'numeric',
                        month: 'long',
                      }).format(new Date(channel.lastLiveAt))}`}
                  {channel.tiktokUserId === null ? ' · владение не подтверждено' : ' · подтверждён'}
                </span>
              </div>

              <div className="flex w-full flex-wrap items-center justify-between gap-2 sm:w-auto sm:justify-end">
                {data?.activeChannelId === channel.id ? (
                  <Badge variant="default">активный</Badge>
                ) : (
                  <Button
                    variant="secondary"
                    size="s"
                    onClick={() => {
                      activate.mutate(channel.id);
                    }}
                  >
                    Сделать активным
                  </Button>
                )}

                <Button
                  variant="ghost"
                  size="s"
                  onClick={() => {
                    setDetaching(channel);
                  }}
                >
                  Отвязать
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="Настройки не привязаны к каналу">
        <p className="text-foreground/80 text-sm">
          Привязки действий, раскладка overlay, виджеты и банки вопросов хранятся на аккаунте.
          Переключение канала ничего не теряет: канал заблокировали — завели новый, всё на месте.
          Скоро: пресет «канал → набор настроек», чтобы держать разные каналы под разные игры.
        </p>
      </Panel>

      <Modal
        open={detaching !== null}
        onClose={() => {
          setDetaching(null);
        }}
        title={`Отвязать @${detaching?.handle ?? ''}?`}
        actions={
          <>
            <Button
              variant="danger-solid"
              size="m"
              loading={detach.isPending}
              onClick={() => {
                if (detaching === null) {
                  return;
                }

                detach.mutate(detaching.id, {
                  onSuccess: () => {
                    toast.show({ tone: 'success', title: 'Канал отвязан' });
                  },
                  onSettled: () => {
                    setDetaching(null);
                  },
                });
              }}
            >
              Отвязать
            </Button>
            <Button
              variant="secondary"
              size="m"
              onClick={() => {
                setDetaching(null);
              }}
            >
              Отмена
            </Button>
          </>
        }
      >
        Вернуть этот канал на ваш аккаунт можно в любой момент без ожидания. История эфиров
        сохранится — она нужна, чтобы канал нельзя было тут же перенести на чужой аккаунт.
      </Modal>
    </div>
  );
}
